import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddIcon from '@mui/icons-material/Add';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ShoppingBasketOutlinedIcon from '@mui/icons-material/ShoppingBasketOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { EmptyState, NumberField, SectionCard, StatCard, currency, number } from './ui';

type Tier = { from: number | null; bonus: number | null };
type Program = { active: boolean; tiers: { from: number; bonus: number }[]; bonus_days: number | null; online_topup: boolean; online_available: boolean };
type Summary = { topups: Record<string, number>; topups_total: number; bonus_given: number; spent: number; outstanding: number; outstanding_paid: number; customers: number };
type Holder = { client: number; name: string; phone: string; balance: number };

const METHODS: Record<string, string> = { cash: 'efectivo', card: 'tarjeta', online: 'en línea' };
const BONUS_DAYS = [null, 30, 60, 90, 180, 365];

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.response === 'string') return data.response;
  return fallback;
};

/**
 * Panel › Saldo: the business's prepaid balance ("recarga $500, recibe $550"): on/off, the bonus per amount, how long
 * a bonus lasts and topping up online; this month's top-ups and the balance customers hold.
 */
const Balance = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const [program, setProgram] = React.useState<Program | null>(null);
  const [draft, setDraft] = React.useState<{ active: boolean; tiers: Tier[]; bonus_days: number | null; online_topup: boolean } | null>(null);
  const [summary, setSummary] = React.useState<Summary | null>(null);
  const [holders, setHolders] = React.useState<Holder[] | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const toDraft = (p: Program) => ({ active: p.active, tiers: p.tiers.map(t => ({ ...t })), bonus_days: p.bonus_days, online_topup: p.online_topup });
  React.useEffect(() => {
    axios.get('api/balance/program/').then(({ data }) => { setProgram(data); setDraft(toDraft(data)); }).catch(() => {});
    axios.get('api/balance/summary/', { params: { start_date: dayjs().startOf('month').toISOString(), end_date: dayjs().endOf('month').toISOString() } })
      .then(({ data }) => setSummary(data)).catch(() => {});
    axios.get('api/balance/customers/').then(({ data }) => setHolders(data)).catch(() => setHolders([]));
  }, [axios]);

  const changed = !!program && !!draft && JSON.stringify(toDraft(program)) !== JSON.stringify(draft);
  const setTier = (index: number, key: keyof Tier, value: number | null) =>
    setDraft(d => d && { ...d, tiers: d.tiers.map((t, i) => (i === index ? { ...t, [key]: value } : t)) });

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setMessage(null);
    try {
      const tiers = draft.tiers.filter(t => t.from != null).map(t => ({ from: t.from, bonus: t.bonus ?? 0 }));
      const { data } = await axios.put('api/balance/program/', { ...draft, tiers });
      setProgram(data);
      setDraft(toDraft(data));
      setMessage({ type: 'success', text: data.active ? 'Guardado. Tus cajas ya pueden recargar saldo.' : 'Guardado. El saldo está apagado.' });
    } catch (err) {
      setMessage({ type: 'error', text: errorText(err, 'No se pudo guardar.') });
    } finally {
      setBusy(false);
    }
  };

  const example = draft?.tiers.filter(t => t.from).sort((a, b) => (b.from ?? 0) - (a.from ?? 0))[0];
  const topupsText = summary && Object.entries(summary.topups).map(([m, v]) => `${currency(v)} ${METHODS[m] ?? m}`).join(' · ');

  return (
    <PageShell title="Saldo" subtitle="Tus clientes recargan saldo por adelantado, reciben un regalo y pagan con él en caja."
      actions={<Button variant="contained" onClick={save} disabled={!changed || busy}>{busy ? 'Guardando…' : 'Guardar cambios'}</Button>}>
      {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>{message.text}</Alert>}
      <Grid container spacing={3}>
        {[
          { label: 'Recargas del mes', value: currency(summary?.topups_total), icon: <PaymentsOutlinedIcon /> },
          { label: 'Regalo entregado', value: currency(summary?.bonus_given), icon: <CardGiftcardOutlinedIcon /> },
          { label: 'Pagado con saldo', value: currency(summary?.spent), icon: <ShoppingBasketOutlinedIcon /> },
          { label: 'Saldo de tus clientes', value: currency(summary?.outstanding), icon: <AccountBalanceWalletOutlinedIcon /> },
        ].map(card => (
          <Grid key={card.label} size={{ xs: 6, lg: 3 }}>
            <StatCard label={card.label} value={card.value} icon={card.icon} loading={!summary} />
          </Grid>
        ))}

        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title="Cómo funciona en tu negocio"
            subtitle="Lo que tus clientes pagan nunca vence y es lo primero que usan; el regalo puede vencer.">
            {draft && (
              <Stack spacing={2.5}>
                <FormControlLabel control={<Switch checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} data-testid="balance-active" />}
                  label={<Box><Typography>Recibir recargas de saldo</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>En caja: «Recargar saldo» y pagar con saldo. Si lo apagas, tus clientes conservan lo que tienen y lo siguen usando.</Typography></Box>} />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Regalo por recarga</Typography>
                  <Stack spacing={1.5}>
                    {draft.tiers.map((tier, index) => (
                      <Stack key={index} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <NumberField size="small" label="Al recargar desde ($)" value={tier.from} onChange={v => setTier(index, 'from', v)} sx={{ flex: 1 }} />
                        <NumberField size="small" label="Regalo ($)" value={tier.bonus} onChange={v => setTier(index, 'bonus', v)} sx={{ flex: 1 }} />
                        <IconButton aria-label="Quitar" onClick={() => setDraft({ ...draft, tiers: draft.tiers.filter((_, i) => i !== index) })}><DeleteOutlineIcon /></IconButton>
                      </Stack>
                    ))}
                    <Box>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setDraft({ ...draft, tiers: [...draft.tiers, { from: null, bonus: null }] })}
                        disabled={draft.tiers.length >= 10}>Agregar nivel</Button>
                    </Box>
                    {example?.from ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Ejemplo: recarga {currency(example.from)} y recibe {currency(example.from + (example.bonus ?? 0))}. Cada recarga recibe el regalo del nivel más alto que alcanza.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin niveles, las recargas no llevan regalo.</Typography>
                    )}
                  </Stack>
                </Box>

                <TextField select size="small" label="El regalo dura" value={draft.bonus_days ?? ''} sx={{ maxWidth: 280 }}
                  onChange={e => setDraft({ ...draft, bonus_days: e.target.value === '' ? null : Number(e.target.value) })}>
                  {BONUS_DAYS.map(days => <MenuItem key={String(days)} value={days ?? ''}>{days ? `${days} días` : 'No vence'}</MenuItem>)}
                </TextField>

                <FormControlLabel disabled={!program?.online_available && !draft.online_topup}
                  control={<Switch checked={draft.online_topup} onChange={e => setDraft({ ...draft, online_topup: e.target.checked })} />}
                  label={<Box><Typography>Recargas en línea desde la tarjeta del cliente</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {program?.online_available
                        ? 'Pagan en línea y el saldo les llega al instante, con su regalo.'
                        : 'Conecta Mercado Pago (u otro proveedor de pagos en línea) en Configuración para recibirlas.'}
                    </Typography></Box>} />
              </Stack>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard title="Clientes con saldo" subtitle={summary ? `${number(summary.customers)} ${summary.customers === 1 ? 'cliente' : 'clientes'}${topupsText ? ` · este mes: ${topupsText}` : ''}` : undefined} noPadding>
            {holders && holders.length === 0 ? (
              <EmptyState compact icon={<AccountBalanceWalletOutlinedIcon />} title="Nadie tiene saldo todavía"
                description="Cuando un cliente recargue en caja o en línea, aparece aquí." />
            ) : (
              <Table size="small">
                <TableHead><TableRow><TableCell>Cliente</TableCell><TableCell align="right">Saldo</TableCell></TableRow></TableHead>
                <TableBody>
                  {(holders ?? []).map(h => (
                    <TableRow key={h.client} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/clients/${h.client}`)}>
                      <TableCell>{h.name || 'Sin nombre'}<Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>{h.phone}</Typography></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(h.balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default Balance;
